import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaTruck,
  FaWeightHanging,
  FaChartLine,
  FaEnvelope,
} from 'react-icons/fa';
import './RequestDetail.css';
import { getRequestById } from '../../services/requestsApi';
import { SERVICE_TYPE_LABELS } from '../../utils/feedItems';
import {
  MapContainer,
  TileLayer,
  Polyline,
  CircleMarker,
  Popup,
  useMap,
} from 'react-leaflet';
import { resolveLocationLabel } from '../../services/unlocodeService';

const MAP_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const MAP_TILE_ATTRIBUTION = '&copy; OpenStreetMap contributors';
const MAP_DEFAULT_CENTER = [23.6345, -102.5528];
const MAP_DEFAULT_ZOOM = 5;

const deg2rad = (value) => (value * Math.PI) / 180;
const rad2deg = (value) => (value * 180) / Math.PI;

function RouteBounds({ pickup, dropoff }) {
  const map = useMap();

  useEffect(() => {
    const points = [];
    if (pickup) {
      points.push([pickup.lat, pickup.lng]);
    }
    if (dropoff) {
      points.push([dropoff.lat, dropoff.lng]);
    }
    if (!points.length) {
      return;
    }
    if (points.length === 1) {
      map.setView(points[0], 6);
      return;
    }
    map.fitBounds(points, { padding: [32, 32], maxZoom: 9 });
  }, [pickup, dropoff, map]);

  return null;
}

const formatDate = value => {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const toTitleCase = text => {
  if (!text) return '';
  return text
    .split(/\s+/)
    .filter(Boolean)
    .map(word => {
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

const DEFAULT_REQUEST_CURRENCY = 'MXN';

const normalizeCurrencyCode = currency => {
  if (typeof currency === 'string' && currency.trim().length === 3) {
    return currency.trim().toUpperCase();
  }
  return DEFAULT_REQUEST_CURRENCY;
};

const formatBudgetLabel = (value, currency) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const normalizedCurrency = normalizeCurrencyCode(currency);
  const numeric = Number(String(value).replace(/[^\d.-]/g, ''));
  if (Number.isFinite(numeric)) {
    const safeNumeric = Math.max(0, numeric);
    const locale = normalizedCurrency === 'USD' ? 'en-US' : 'es-MX';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 0,
    }).format(safeNumeric);
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return String(value);
};

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [route, setRoute] = useState({
    loading: false,
    pickup: null,
    dropoff: null,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id) {
        setError('Solicitud no encontrada.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const data = await getRequestById(id);
        if (!cancelled) {
          setRequest(data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('No se pudo cargar la solicitud.', err);
          setError('No pudimos cargar los detalles de la solicitud.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const serviceLabel =
    (request && SERVICE_TYPE_LABELS[request.serviceType]) || 'Servicio';
  const company = request?.company;
  const companyHref = company?.slug ? `/empresa/${company.slug}` : '';
  const neededByLabel = formatDate(request?.neededBy);
  const createdLabel = formatDate(request?.created);
  const requestIdLabel = request?.id || id;
  const weightLabel = request?.weight || request?.cargoWeight || '';
  const unitTypeLabel = request?.unitType || request?.vehicleType || '';
  const serviceLevelLabel = request?.serviceLevel || request?.priority || '';

  const budgetLabel = useMemo(
    () => formatBudgetLabel(request?.budget, request?.currency),
    [request?.budget, request?.currency]
  );

  const primarySpecs = useMemo(() => {
    return [
      {
        icon: <FaCalendarAlt aria-hidden="true" />,
        label: 'Fecha requerida',
        value: neededByLabel || 'Por definir',
      },
      {
        icon: <FaWeightHanging aria-hidden="true" />,
        label: 'Peso estimado',
        value: weightLabel || 'Por definir',
      },
      {
        icon: <FaTruck aria-hidden="true" />,
        label: 'Tipo de unidad',
        value: unitTypeLabel || 'Por definir',
      },
      {
        icon: <FaChartLine aria-hidden="true" />,
        label: 'Nivel de servicio',
        value: serviceLevelLabel || 'Por definir',
      },
    ];
  }, [neededByLabel, weightLabel, unitTypeLabel, serviceLevelLabel]);

  const displayTitle = useMemo(() => {
    const base = request?.title?.trim();
    if (!base) return 'Oportunidad sin título';
    return toTitleCase(base);
  }, [request?.title]);

  useEffect(() => {
    let cancelled = false;

    const loadRoutePoints = async () => {
      if (!request) {
        setRoute(prev => ({ ...prev, pickup: null, dropoff: null }));
        return;
      }

      const pickupLabel = request.pickupLocation?.trim();
      const dropoffLabel = request.dropoffLocation?.trim();

      if (!pickupLabel && !dropoffLabel) {
        setRoute({ loading: false, pickup: null, dropoff: null });
        return;
      }

      setRoute(prev => ({ ...prev, loading: true }));

      try {
        const [pickupEntry, dropoffEntry] = await Promise.all([
          pickupLabel ? resolveLocationLabel(pickupLabel) : Promise.resolve(null),
          dropoffLabel ? resolveLocationLabel(dropoffLabel) : Promise.resolve(null),
        ]);

        if (cancelled) return;

        setRoute({
          loading: false,
          pickup: pickupEntry && pickupEntry.latitude != null && pickupEntry.longitude != null
            ? {
                lat: pickupEntry.latitude,
                lng: pickupEntry.longitude,
                label: pickupLabel || pickupEntry.display || pickupEntry.name,
              }
            : null,
          dropoff: dropoffEntry && dropoffEntry.latitude != null && dropoffEntry.longitude != null
            ? {
                lat: dropoffEntry.latitude,
                lng: dropoffEntry.longitude,
                label: dropoffLabel || dropoffEntry.display || dropoffEntry.name,
              }
            : null,
        });
      } catch (routeError) {
        console.warn('No se pudo resolver la ruta de la solicitud', routeError);
        if (!cancelled) {
          setRoute({
            loading: false,
            pickup: null,
            dropoff: null,
          });
        }
      }
    };

    loadRoutePoints();

    return () => {
      cancelled = true;
    };
  }, [request]);

  const mapCenter = useMemo(() => {
    if (route.pickup && route.dropoff) {
      return [(route.pickup.lat + route.dropoff.lat) / 2, (route.pickup.lng + route.dropoff.lng) / 2];
    }
    if (route.pickup) {
      return [route.pickup.lat, route.pickup.lng];
    }
    if (route.dropoff) {
      return [route.dropoff.lat, route.dropoff.lng];
    }
    return MAP_DEFAULT_CENTER;
  }, [route.pickup, route.dropoff]);

  const polylinePositions = useMemo(() => {
    if (!route.pickup || !route.dropoff) return null;

    const lat1 = deg2rad(route.pickup.lat);
    const lon1 = deg2rad(route.pickup.lng);
    const lat2 = deg2rad(route.dropoff.lat);
    const lon2 = deg2rad(route.dropoff.lng);

    const delta = 2 * Math.asin(
      Math.sqrt(
        Math.sin((lat2 - lat1) / 2) ** 2 +
          Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2
      )
    );

    if (delta === 0 || Number.isNaN(delta)) {
      return [
        [route.pickup.lat, route.pickup.lng],
        [route.dropoff.lat, route.dropoff.lng],
      ];
    }

    const sinDelta = Math.sin(delta) || 1;
    const segments = 48;
    const points = [];

    for (let i = 0; i <= segments; i += 1) {
      const fraction = i / segments;
      const A = Math.sin((1 - fraction) * delta) / sinDelta;
      const B = Math.sin(fraction * delta) / sinDelta;

      const x =
        A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
      const y =
        A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
      const z = A * Math.sin(lat1) + B * Math.sin(lat2);

      const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
      const lon = Math.atan2(y, x);

      points.push([rad2deg(lat), rad2deg(lon)]);
    }
    return points;
  }, [route.pickup, route.dropoff]);

  const canSendProposal = Boolean(request?.creator?.email);

  const handleSendProposal = () => {
    if (!canSendProposal) return;

    const recipient = request.creator.email;
    const subject = encodeURIComponent(`Propuesta para ${request.title || 'solicitud'}`);
    const bodyLines = [
      `Hola ${request.creator?.name || ''}`.trim(),
      '',
      'Estoy interesado en atender la oportunidad y me gustaría compartir una propuesta.',
      '¿Podemos coordinar una llamada para revisar el alcance y la disponibilidad?',
      '',
      'Gracias, quedo atento.',
    ];
    const body = encodeURIComponent(bodyLines.join('\n'));
    window.location.href = `mailto:${recipient}?subject=${subject}&body=${body}`;
  };

  return (
    <main className="request-detail" aria-live="polite">
      <div className="request-detail__shell">
        <button
          type="button"
          className="request-detail__back"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft aria-hidden="true" /> Regresar
        </button>
        {loading ? (
          <div className="request-detail__loading">Cargando solicitud…</div>
        ) : null}
        {error ? <p className="request-detail__error">{error}</p> : null}

        {!loading && !error && request ? (
          <div className="request-grid">
            <section className="request-grid__context request-mission-card" aria-label="Contexto operativo">
              <header className="request-context__header request-mission-card__header">
                <div className="request-mission-card__header-left">
                  <span className="request-context__slug">Solicitud #{requestIdLabel}</span>
                  <div className="request-mission-card__title-row">
                    <h1>{displayTitle}</h1>
                    <div className="request-context__badges">
                      {request.status ? (
                        <span className={`request-context__badge request-context__badge--${request.status}`}>
                          {request.status}
                        </span>
                      ) : null}
                      <span className="request-context__badge request-context__badge--muted">{serviceLabel}</span>
                    </div>
                  </div>
                </div>
                {createdLabel ? (
                  <p className="request-mission-card__date">Publicada el {createdLabel}</p>
                ) : null}
              </header>

              <section className="request-route-map" aria-label="Ruta y mapa">
                <div className="request-route-map__header request-mission-card__route-bar">
                  {request.pickupLocation || request.dropoffLocation ? (
                    <div className="request-route-card__content">
                      <div className="route-node">
                        <span className="route-node__icon route-node__icon--origin" aria-hidden="true" />
                        <div>
                          <span className="route-node__label">Origen</span>
                          <strong className="route-node__city">{request.pickupLocation || 'Por definir'}</strong>
                        </div>
                      </div>
                      <div className="route-connector route-connector--hero" aria-hidden="true">
                        <span />
                      </div>
                      <div className="route-node route-node--destination">
                        <span className="route-node__icon route-node__icon--destination" aria-hidden="true" />
                        <div>
                          <span className="route-node__label">Destino</span>
                          <strong className="route-node__city">{request.dropoffLocation || 'Por definir'}</strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="request-card__empty">
                      El solicitante no compartió ubicaciones específicas para esta solicitud.
                    </p>
                  )}
                </div>
                <div className="request-card__map request-mission-card__map">
                  <div className="request-card__map-frame">
                    {route.loading ? (
                      <p className="request-card__empty">Calculando ubicaciones…</p>
                    ) : (route.pickup || route.dropoff) ? (
                      <>
                        <MapContainer
                          center={mapCenter}
                          zoom={polylinePositions ? 5 : MAP_DEFAULT_ZOOM}
                          scrollWheelZoom={false}
                          className="request-card__leaflet"
                        >
                          <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
                          <RouteBounds pickup={route.pickup} dropoff={route.dropoff} />
                          {route.pickup ? (
                            <CircleMarker
                              center={[route.pickup.lat, route.pickup.lng]}
                              pathOptions={{
                                color: '#2563eb',
                                fillColor: '#2563eb',
                                fillOpacity: 0.7,
                                weight: 2,
                              }}
                              radius={10}
                            >
                              <Popup>
                                <strong>Origen</strong>
                                <p>{route.pickup.label}</p>
                              </Popup>
                            </CircleMarker>
                          ) : null}
                          {route.dropoff ? (
                            <CircleMarker
                              center={[route.dropoff.lat, route.dropoff.lng]}
                              pathOptions={{
                                color: '#16a34a',
                                fillColor: '#16a34a',
                                fillOpacity: 0.7,
                                weight: 2,
                              }}
                              radius={10}
                            >
                              <Popup>
                                <strong>Destino</strong>
                                <p>{route.dropoff.label}</p>
                              </Popup>
                            </CircleMarker>
                          ) : null}
                          {polylinePositions ? (
                            <Polyline
                              positions={polylinePositions}
                              pathOptions={{
                                color: '#1c64f2',
                                weight: 4,
                                opacity: 0.7,
                                dashArray: '8 10',
                              }}
                            />
                          ) : null}
                        </MapContainer>
                        <div className="request-card__map-legend">
                          {route.pickup ? (
                            <span>
                              <span className="request-card__map-dot request-card__map-dot--pickup" />
                              Origen
                            </span>
                          ) : null}
                          {route.dropoff ? (
                            <span>
                              <span className="request-card__map-dot request-card__map-dot--dropoff" />
                              Destino
                            </span>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <p className="request-card__empty">
                        Esta solicitud no cuenta con ubicaciones georreferenciadas para mostrar el mapa.
                      </p>
                    )}
                  </div>
                </div>
              </section>

              <section className="request-mission-card__specs" aria-label="Especificaciones clave">
                {primarySpecs.map(entry => (
                  <div key={entry.label} className="mission-spec">
                    <span className="mission-spec__icon">{entry.icon}</span>
                    <div>
                      <span className="mission-spec__label">{entry.label}</span>
                      <span className="mission-spec__value">{entry.value}</span>
                    </div>
                  </div>
                ))}
              </section>

              {request.details ? (
                <section className="request-card__notes">
                  <h3>Notas del solicitante</h3>
                  {request.details
                    .split(/\n+/)
                    .map((paragraph, index) =>
                      paragraph.trim() ? <p key={`note-${index}`}>{paragraph.trim()}</p> : null
                    )}
                </section>
              ) : null}
            </section>

            <aside className="request-grid__sidebar" aria-label="Consola de negociación">
              <div className="request-deal-card">
                <span className="request-deal-card__label">Presupuesto objetivo</span>
                <strong className="request-deal-card__price">{budgetLabel || 'Por definir'}</strong>
                <button
                  type="button"
                  className="request-deal-card__cta"
                  onClick={handleSendProposal}
                  disabled={!canSendProposal}
                >
                  Enviar propuesta
                </button>
                <div className="request-deal-card__actions">
                  <button type="button" className="request-deal-card__ghost">
                    Guardar
                  </button>
                  <button type="button" className="request-deal-card__ghost">
                    Compartir
                  </button>
                </div>
              </div>

              <div className="request-client-card">
                <p className="request-client-card__label">Perfil del cliente</p>
                <div className="request-client-card__identity">
                  <div className="request-client-card__logo" aria-hidden="true">
                    {company?.logoUrl ? (
                      <img src={company.logoUrl} alt={company.name} loading="lazy" />
                    ) : (
                      <span>{(company?.name || 'E')[0]}</span>
                    )}
                  </div>
                  <div className="request-client-card__names">
                    <strong>{company?.name || 'Empresa no disponible'}</strong>
                    {request.creator?.name ? (
                      <span className="request-client-card__contact-name">{request.creator.name}</span>
                    ) : company?.category ? (
                      <span className="request-client-card__meta">{company.category}</span>
                    ) : null}
                    {companyHref ? <Link to={companyHref}>Ver perfil</Link> : null}
                  </div>
                </div>
                {request.creator?.email ? (
                  <a className="request-client-card__email" href={`mailto:${request.creator.email}`}>
                    <FaEnvelope aria-hidden="true" />
                    {request.creator.email}
                  </a>
                ) : null}
              </div>
            </aside>
          </div>
        ) : null}
      </div>
    </main>
  );
}
